import axios, { type InternalAxiosRequestConfig } from 'axios';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://localhost:7001/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000, // 30 seconds — Start My Day can take 10+ seconds on first sync
});

// Token is injected by the auth interceptor set up in App.tsx
// This allows access to the React auth context from outside components.
// AuthGate sets it while someone is signed in and clears it (null) when the
// signed-in app unmounts; sign-out clears it too.
let tokenGetter: (() => Promise<string>) | null = null;

export function setTokenGetter(getter: (() => Promise<string>) | null) {
  tokenGetter = getter;
}

// The server checks every signed-in request against the person as they are
// now, so a token that hasn't expired can still be refused (401): deactivated,
// signed out elsewhere, two-step turned on in another tab. This renews it
// once, given the refused token, and signs out when that's refused too.
// AuthGate sets it alongside the token getter.
let sessionRefusedHandler: ((refusedToken?: string) => Promise<string>) | null = null;

export function setSessionRefusedHandler(handler: ((refusedToken?: string) => Promise<string>) | null) {
  sessionRefusedHandler = handler;
}

/** A request retried with a renewed token after a 401: it keeps that token. */
type RenewableConfig = InternalAxiosRequestConfig & { renewedAfterRefusal?: boolean };

// ---------------------------------------------------------------------------
// Public sign-in / sign-up calls. These never carry a session or an
// acting-for executive: after in-app navigation to /signup, /join or a reset
// page, the module state above still held the previous person's token getter
// and the EA's executive, and the server credited a new organisation's user to
// someone in another one. Matched on the path (relative to the API base, so an
// absolute URL's leading /api is ignored), case-insensitively, without the
// query or fragment. Every one is [AllowAnonymous] on the server.
// ---------------------------------------------------------------------------

const PUBLIC_AUTH_PATHS = new Set([
  '/auth/login',
  '/auth/mfa/verify',
  '/auth/magic-link',
  '/auth/magic-link/verify',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/passkey/login-options',
  '/auth/passkey/login',
  '/auth/refresh',
  '/auth/microsoft/confirm',
  '/auth/welcome/preview',
  '/auth/welcome/accept',
  '/auth/desktop/initiate',
  '/auth/desktop/exchange',
  '/invitations/preview',
  '/invitations/accept',
  '/billing/plans',
  '/billing/checkout',
  '/onboarding/setup',
  '/appstorebilling/begin-signup',
  '/appstorebilling/provision',
  '/sso/login',
  '/sso/start',
  '/sso/callback',
]);

const PUBLIC_AUTH_PREFIXES = [
  '/billing/checkout-session/',
  '/auth/login/', // social sign-in starts: /auth/login/microsoft, google, apple
  '/auth/oauth-complete/',
];

/** The request's path relative to the API base: lower case, no query, no trailing slash. */
function apiPathOf(url: string): string | null {
  let path: string;
  try {
    // A relative url resolves against the API base (axios joins them), and an
    // absolute one keeps its own path; dot segments are resolved either way.
    path = new URL(url.trim().replace(/^\/{2,}/, '/'), 'http://api.invalid/').pathname;
  } catch {
    return null;
  }
  try { path = decodeURIComponent(path); } catch { /* keep it encoded */ }
  path = path.toLowerCase().replace(/\/{2,}/g, '/').replace(/\/+$/, '');
  if (path === '/api' || path.startsWith('/api/')) path = path.slice(4);
  return path || '/';
}

/** True for the anonymous sign-in / sign-up calls that must go without a session. */
export function isPublicAuthPath(url: string | null | undefined): boolean {
  if (!url) return false;
  const path = apiPathOf(url);
  if (!path) return false;
  return PUBLIC_AUTH_PATHS.has(path) || PUBLIC_AUTH_PREFIXES.some(prefix => path.startsWith(prefix));
}

const ACTING_FOR_PARAM = 'forexecutiveuserid';

/** Removes any Authorization header and acting-for executive from a public call. */
function withoutSession(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const headers = config.headers as unknown as (Record<string, unknown> & { delete?: (name: string) => unknown }) | undefined;
  if (headers) {
    if (typeof headers.delete === 'function') headers.delete('Authorization');
    for (const name of Object.keys(headers)) {
      if (name.toLowerCase() === 'authorization') delete headers[name];
    }
  }

  const params = config.params;
  if (params instanceof URLSearchParams) {
    Array.from(params.keys())
      .filter(name => name.toLowerCase() === ACTING_FOR_PARAM)
      .forEach(name => params.delete(name));
  } else if (params && typeof params === 'object') {
    const kept = { ...params };
    for (const name of Object.keys(kept)) {
      if (name.toLowerCase() === ACTING_FOR_PARAM) delete kept[name];
    }
    config.params = kept;
  }

  // …and one written into the address itself.
  if (config.url && config.url.toLowerCase().includes(ACTING_FOR_PARAM)) {
    const [beforeHash, ...hash] = config.url.split('#');
    const queryAt = beforeHash.indexOf('?');
    if (queryAt >= 0) {
      const nameOf = (pair: string) => {
        const name = pair.split('=')[0];
        try { return decodeURIComponent(name).toLowerCase(); } catch { return name.toLowerCase(); }
      };
      const kept = beforeHash.slice(queryAt + 1).split('&')
        .filter(pair => pair && nameOf(pair) !== ACTING_FOR_PARAM);
      config.url = beforeHash.slice(0, queryAt)
        + (kept.length ? `?${kept.join('&')}` : '')
        + (hash.length ? `#${hash.join('#')}` : '');
    }
  }
  return config;
}

// Principal context — when an EA selects a principal, this ID is appended
// to all API requests as forExecutiveUserId. Set by PrincipalContext.
let _activePrincipalId: string | null = null;

/** Fired when an EA switches the executive they act for. */
export const PRINCIPAL_CHANGED_EVENT = 'cadence:principal-changed';

/** The executive an EA is acting for, or null for the signed-in person. */
export function getActivePrincipalId(): string | null {
  return _activePrincipalId;
}

export function setActivePrincipalId(id: string | null) {
  if (id === _activePrincipalId) return;
  _activePrincipalId = id;
  // Per-executive answers (the AI usage on the entitlements call) follow the switch.
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(PRINCIPAL_CHANGED_EVENT));
}

apiClient.interceptors.request.use(async (config) => {
  // Sign-in and sign-up go as nobody: the token getter isn't even asked (a
  // stale one could refresh or sign out the previous person), and no
  // acting-for executive goes with them.
  if (isPublicAuthPath(config.url)) return withoutSession(config);

  // Auth token injection (a retry after a 401 already carries its renewed one)
  if (tokenGetter && !(config as RenewableConfig).renewedAfterRefusal) {
    try {
      const token = await tokenGetter();
      config.headers.Authorization = `Bearer ${token}`;
    } catch {
      // Token expired — will get 401, handled by response interceptor
    }
  }

  // Principal context injection — append forExecutiveUserId to all requests
  // when an EA has selected a principal. Does NOT override explicit params,
  // and an explicit `forExecutiveUserId: null` means "for myself" (axios
  // leaves null params out of the URL): an EA's own setup must never touch
  // the executive they happen to be viewing.
  if (_activePrincipalId && config.params?.forExecutiveUserId === undefined) {
    config.params = { ...config.params, forExecutiveUserId: _activePrincipalId };
  }

  return config;
});

// Blob.text() where the browser has it; FileReader otherwise.
function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

// Response interceptor — catches the backend's MFA-required wall.
// `RequireMfaCompleteMiddleware` returns 403 with body
// `{ error: "mfa_setup_required", message: ... }` for admin-class users
// whose token carries `mfa_pending=true` AND who hit a non-allowed
// endpoint (everything except /api/auth/mfa/*, /auth/refresh, /auth/me,
// /auth/logout). We catch the 403 globally and redirect to /mfa-setup
// so the user can't get stuck on a page that 403s without any cue —
// the redirect IS the cue.
//
// Idempotent: if we're already on /mfa-setup don't redirect again
// (would loop on the setup page's own GET /me call). The setup page's
// allowed endpoints don't return this error, but the guard is cheap.
//
// Also normalises a tenant-permission refusal (403 `{ error, code:
// "permission_denied", permission }` — an admin set this user's permission to
// No on the Permissions screen): a download (responseType 'blob') gets its
// JSON body parsed so `response.data.error` is readable like any other call,
// and `error.message` carries the server's explanation, so a page that shows
// either tells the user why rather than "Request failed with status code 403".
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const body = error?.response?.data;
    // A signed-in request refused: renew the token once and try again; when
    // renewing fails the person has been signed out (the handler does it).
    const config = error?.config as RenewableConfig | undefined;
    if (status === 401 && config && !config.renewedAfterRefusal && sessionRefusedHandler
        && !isPublicAuthPath(config.url)) {
      config.renewedAfterRefusal = true;
      const sent = config.headers?.Authorization;
      const refused = typeof sent === 'string' && sent.startsWith('Bearer ') ? sent.slice('Bearer '.length) : undefined;
      try {
        const token = await sessionRefusedHandler(refused);
        config.headers.Authorization = `Bearer ${token}`;
      } catch {
        return Promise.reject(error);
      }
      return apiClient(config);
    }
    if (status === 403 && typeof Blob !== 'undefined' && body instanceof Blob && body.type.includes('json')) {
      try {
        error.response.data = JSON.parse(await readBlobText(body));
      } catch {
        // Not JSON after all — leave the blob as it was.
      }
    }
    const code = error?.response?.data?.error;
    if (status === 403 && code === 'mfa_setup_required') {
      if (typeof window !== 'undefined'
          && window.location.pathname !== '/mfa-setup') {
        window.location.replace('/mfa-setup');
      }
    } else if (status === 403 || status === 429) {
      await announcePlanLimit(error);
    }
    if (status === 403 && error?.response?.data?.code === 'permission_denied') {
      if (typeof error.response.data.error === 'string') error.message = error.response.data.error;
      announcePermissionDenied(error.response.data.permission);
    }
    return Promise.reject(error);
  },
);

/**
 * Fired on every permission_denied refusal, with the permission's name
 * ("CanViewReports"). PermissionsProvider re-reads /permissions/me when its
 * copy said the user had it (an admin changed it meanwhile).
 */
export const PERMISSION_DENIED_EVENT = 'cadence:permission-denied';

function announcePermissionDenied(permission: unknown) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<{ permission?: string }>(PERMISSION_DENIED_EVENT, {
    detail: { permission: typeof permission === 'string' ? permission : undefined },
  }));
}

// ---------------------------------------------------------------------------
// Plan limits. The feature gate answers 403 `{ error, code:
// "plan_upgrade_required", entitlement, requiredPlan }` for a feature the
// plan doesn't include, and the AI allowance answers 429 `{ error, code:
// "ai_allowance_exceeded", resetsAt }` at the hard stop. Both are announced
// as one window event that the single mounted <UpgradePrompt /> turns into a
// dialog. The rejection still reaches the caller untouched, so per-screen
// handling (Admin's inline SSO message, a page's own toast) keeps working.
// ---------------------------------------------------------------------------

export const PLAN_LIMIT_EVENT = 'cadence:plan-limit';

export type PlanLimitCode = 'plan_upgrade_required' | 'ai_allowance_exceeded' | 'transcript_limit_reached';

export interface PlanLimitDetail {
  code: PlanLimitCode;
  /** The server's own sentence, e.g. "This needs the Starter plan or above." */
  message: string;
  /** PascalCase entitlement name from the 403 body, e.g. "StrategicDrivers". */
  entitlement?: string;
  requiredPlan?: string;
  /** When the AI allowance resets (429 only). */
  resetsAt?: string;
  /**
   * True for a read the page made on its own (a GET that isn't a download).
   * Those don't open a dialog: the route guard and the hidden widgets already
   * explain a locked feature, and a background fetch shouldn't interrupt.
   */
  background: boolean;
}

// A download (responseType "blob") delivers its error body as a Blob too.
async function readBody(data: unknown): Promise<any> {
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try { return JSON.parse(await data.text()); } catch { return null; }
  }
  return data;
}

async function announcePlanLimit(error: any): Promise<void> {
  if (typeof window === 'undefined') return;
  const body = await readBody(error?.response?.data);
  if (!body || typeof body !== 'object') return;
  const code: string | undefined = body.code ?? body.error;
  const status = error?.response?.status;
  const isUpgrade = status === 403 && code === 'plan_upgrade_required';
  // The monthly transcript limit is an allowance too: same dialog, its own sentence.
  const isAllowance = status === 429 && (code === 'ai_allowance_exceeded' || code === 'transcript_limit_reached');
  if (!isUpgrade && !isAllowance) return;

  const config = error?.config ?? {};
  const method = String(config.method ?? 'get').toLowerCase();
  const detail: PlanLimitDetail = {
    code: isUpgrade ? 'plan_upgrade_required'
      : code === 'transcript_limit_reached' ? 'transcript_limit_reached' : 'ai_allowance_exceeded',
    message: typeof body.error === 'string' && body.error !== code
      ? body.error
      : (typeof body.message === 'string' ? body.message : ''),
    entitlement: typeof body.entitlement === 'string' ? body.entitlement : undefined,
    requiredPlan: typeof body.requiredPlan === 'string' ? body.requiredPlan : undefined,
    resetsAt: typeof body.resetsAt === 'string' ? body.resetsAt : undefined,
    // AI requests are always something the user asked for.
    background: isUpgrade && method === 'get' && config.responseType !== 'blob',
  };
  window.dispatchEvent(new CustomEvent<PlanLimitDetail>(PLAN_LIMIT_EVENT, { detail }));
}

export default apiClient;
